import {IChatHistory, IFunctionCallLog, IUiUpdateLog} from '../../../models/conversation.model';

const DEFAULT_PRODUCT_CARD_PRESENTATION: ConversationProductCardPresentation = 'horizontal';
const INLINE_DISPLAY_MODE = 'inline';
const BLOCK_DISPLAY_MODE = 'block';

export type ConversationProductCardPresentation =
  | 'horizontal'
  | 'horizontal-compact'
  | 'vertical'
  | 'vertical-list'
  | 'vertical-carousel'
  | 'compact-carousel';

export interface ConversationProductCardView {
  id: string;
  sku: string;
  name: string;
  url: string;
  description?: string;
  imageUrl?: string;
  presentation?: ConversationProductCardPresentation;
  priceText?: string;
  price?: string | number;
  priceValue?: string | number;
  salePriceText?: string;
  sale_price_text?: string;
  salePrice?: string | number;
  sale_price?: string | number;
  regularPriceText?: string;
  regular_price_text?: string;
  originalPriceText?: string;
  original_price_text?: string;
  regularPrice?: string | number;
  regular_price?: string | number;
  currency?: string;
  priceCurrency?: string;
  priceSuffix?: string;
}

export interface ConversationTextSegment {
  id: string;
  kind: 'text';
  text: string;
}

export interface ConversationProductCardsSegment {
  id: string;
  kind: 'product-cards';
  source: 'ui-update' | 'function-call';
  presentation: ConversationProductCardPresentation;
  cards: ConversationProductCardView[];
}

export type ConversationTranscriptSegment = ConversationTextSegment | ConversationProductCardsSegment;

export interface ConversationTranscriptOptions {
  fallbackPresentation?: unknown;
  includeFunctionCallFallback?: boolean;
}

interface ReplayProductCardGroup {
  segment: ConversationProductCardsSegment;
  displayMode: string;
  renderPosition: string;
  hasTextOffset: boolean;
  textOffset: number;
  timestamp: number;
}

type UnknownRecord = Record<string, any>;

export function buildConversationTranscriptSegments(
  message: Pick<IChatHistory, 'user' | 'human' | 'text' | 'functionsCalled' | 'uiUpdates'> | null | undefined,
  options: ConversationTranscriptOptions = {}
): ConversationTranscriptSegment[] {
  const text = typeof message?.text === 'string' ? message.text : '';

  if (message?.user || message?.human) {
    return text.length ? [createTextSegment(text, 0)] : [];
  }

  const fallbackPresentation = normalizeProductCardPresentation(
    options.fallbackPresentation || DEFAULT_PRODUCT_CARD_PRESENTATION
  );
  const uiGroups = extractUiProductCardGroups(message?.uiUpdates || [], fallbackPresentation);

  if (uiGroups.length) {
    return buildAssistantSegmentsFromUiUpdates(text, uiGroups);
  }

  const segments: ConversationTranscriptSegment[] = [];
  pushTextSegment(segments, text);

  if (options.includeFunctionCallFallback !== false) {
    segments.push(...extractFunctionProductCardGroups(message?.functionsCalled || [], fallbackPresentation));
  }

  return segments;
}

export function normalizeProductCardPayload(data: unknown): ConversationProductCardView[] {
  const payload = typeof data === 'string' ? tryParseJson(data) : data;
  if (!payload) {
    return [];
  }

  const records = Array.isArray(payload)
    ? payload
    : Array.isArray((payload as UnknownRecord)['cards'])
      ? (payload as UnknownRecord)['cards']
      : [payload];

  return dedupeProductCards((records as unknown[])
    .map(normalizeProductCardRecord)
    .filter((card): card is ConversationProductCardView => !!card));
}

export function resolveProductCardPresentation(
  data: unknown,
  update: UnknownRecord = {},
  fallback: unknown = DEFAULT_PRODUCT_CARD_PRESENTATION
): ConversationProductCardPresentation {
  const payload = typeof data === 'string' ? tryParseJson(data) : data;
  const payloadRecord = isRecord(payload) ? payload : {};
  const cards = Array.isArray(payload)
    ? payload
    : Array.isArray(payloadRecord['cards'])
      ? payloadRecord['cards']
      : isRecord(payload)
        ? [payload]
        : [];
  const firstCard = isRecord(cards[0]) ? cards[0] : {};

  return normalizeProductCardPresentation(firstString(
    update['presentation'],
    update['Presentation'],
    update['layout'],
    update['Layout'],
    update['variant'],
    update['Variant'],
    payloadRecord['presentation'],
    payloadRecord['layout'],
    payloadRecord['variant'],
    payloadRecord['displayVariant'],
    payloadRecord['cardLayout'],
    payloadRecord['cardPresentation'],
    firstCard['presentation'],
    firstCard['layout'],
    firstCard['variant'],
    firstCard['displayVariant'],
    firstCard['cardLayout'],
    firstCard['cardPresentation'],
    fallback
  ));
}

export function normalizeProductCardPresentation(value: unknown): ConversationProductCardPresentation {
  const normalized = normalizePresentationToken(value);

  switch (normalized) {
    case 'compact':
    case 'compressed':
    case 'horizontal-compact':
      return 'horizontal-compact';
    case 'vertical':
      return 'vertical';
    case 'list':
    case 'stacked':
    case 'vertical-list':
    case 'vertical-stacked':
      return 'vertical-list';
    case 'carousel':
    case 'vertical-carousel':
    case 'vertical-carousel-view':
      return 'vertical-carousel';
    case 'active-brands':
    case 'active-brands-carousel':
    case 'compact-carousel':
      return 'compact-carousel';
    case 'horizontal':
    default:
      return DEFAULT_PRODUCT_CARD_PRESENTATION;
  }
}

function buildAssistantSegmentsFromUiUpdates(text: string, uiGroups: ReplayProductCardGroup[]): ConversationTranscriptSegment[] {
  const groupsBeforeText = sortReplayGroups(uiGroups.filter(shouldReplayBeforeAssistantText));
  const groupsBeforeTextSet = new Set(groupsBeforeText);
  const interleavedGroups = uiGroups
    .filter(group =>
      group.displayMode === BLOCK_DISPLAY_MODE &&
      group.renderPosition !== 'assistant-end' &&
      group.hasTextOffset &&
      !groupsBeforeTextSet.has(group)
    )
    .sort((left, right) => {
      const offsetDiff = left.textOffset - right.textOffset;
      return offsetDiff !== 0 ? offsetDiff : left.timestamp - right.timestamp;
    });

  if (!interleavedGroups.length) {
    const segments: ConversationTranscriptSegment[] = [];
    groupsBeforeText.forEach(group => segments.push(group.segment));
    pushTextSegment(segments, text);
    sortReplayGroups(uiGroups.filter(group => !groupsBeforeTextSet.has(group)))
      .forEach(group => segments.push(group.segment));
    return segments;
  }

  const segments: ConversationTranscriptSegment[] = [];
  const consumed = new Set<ReplayProductCardGroup>();
  let cursor = 0;

  groupsBeforeText.forEach(group => {
    consumed.add(group);
    segments.push(group.segment);
  });

  interleavedGroups.forEach(group => {
    consumed.add(group);
    const nextOffset = Math.max(cursor, Math.min(group.textOffset, text.length));
    pushTextSegment(segments, text.slice(cursor, nextOffset));
    segments.push(group.segment);
    cursor = nextOffset;
  });

  pushTextSegment(segments, text.slice(cursor));
  sortReplayGroups(uiGroups.filter(group => !consumed.has(group)))
    .forEach(group => segments.push(group.segment));

  return segments;
}

function sortReplayGroups(groups: ReplayProductCardGroup[]): ReplayProductCardGroup[] {
  return [...groups].sort((left, right) => left.timestamp - right.timestamp);
}

function extractUiProductCardGroups(
  uiUpdates: IUiUpdateLog[],
  fallbackPresentation: ConversationProductCardPresentation
): ReplayProductCardGroup[] {
  return (uiUpdates || [])
    .map((update, index) => readUiProductCardGroup(update, index, fallbackPresentation))
    .filter((group): group is ReplayProductCardGroup => !!group);
}

function readUiProductCardGroup(
  update: IUiUpdateLog,
  index: number,
  fallbackPresentation: ConversationProductCardPresentation
): ReplayProductCardGroup | null {
  const record = update as UnknownRecord;
  const functionName = firstString(record['Function'], record['function']).toLowerCase();
  if (!isProductCardFunctionName(functionName)) {
    return null;
  }

  const displayMode = firstString(record['DisplayMode'], record['displayMode']).toLowerCase();
  if (displayMode === INLINE_DISPLAY_MODE) {
    return null;
  }

  const data = firstDefined(record['DataJson'], record['dataJson'], record['data']);
  const cards = normalizeProductCardPayload(data);
  if (!cards.length) {
    return null;
  }

  const presentation = resolveProductCardPresentation(data, record, fallbackPresentation);
  const textOffset = readTextOffset(record);

  return {
    segment: {
      id: `ui-${firstString(record['MessageId'], record['messageId'], record['TimestampUtc'], record['timestampUtc'], index)}`,
      kind: 'product-cards',
      source: 'ui-update',
      presentation,
      cards
    },
    displayMode,
    renderPosition: firstString(record['RenderPosition'], record['renderPosition']).toLowerCase(),
    hasTextOffset: hasTextOffset(record),
    textOffset,
    timestamp: readTimestamp(record)
  };
}

function extractFunctionProductCardGroups(
  functionsCalled: IFunctionCallLog[],
  fallbackPresentation: ConversationProductCardPresentation
): ConversationProductCardsSegment[] {
  return (functionsCalled || [])
    .map((functionCall, index) => readFunctionProductCardGroup(functionCall, index, fallbackPresentation))
    .filter((group): group is ConversationProductCardsSegment => !!group);
}

function readFunctionProductCardGroup(
  functionCall: IFunctionCallLog,
  index: number,
  fallbackPresentation: ConversationProductCardPresentation
): ConversationProductCardsSegment | null {
  const record = functionCall as UnknownRecord;
  const functionType = firstString(record['FunctionType'], record['functionType']).toLowerCase();
  const functionName = firstString(record['Name'], record['name']).toLowerCase();

  if (functionType !== 'aiproductcards' && !isProductCardFunctionName(functionName)) {
    return null;
  }

  const data = firstDefined(record['ArgumentsPreview'], record['argumentsPreview'], record['argumentsJson']);
  const cards = normalizeProductCardPayload(data);
  if (!cards.length) {
    return null;
  }

  const payload = typeof data === 'string' ? tryParseJson(data) : data;
  const presentation = resolveProductCardPresentation(payload, record, fallbackPresentation);

  return {
    id: `function-${firstString(record['CallId'], record['callId'], record['Name'], record['name'], index)}`,
    kind: 'product-cards',
    source: 'function-call',
    presentation,
    cards
  };
}

function shouldReplayBeforeAssistantText(group: ReplayProductCardGroup): boolean {
  return group.renderPosition === 'assistant-end' && group.segment.presentation === 'compact-carousel';
}

function pushTextSegment(segments: ConversationTranscriptSegment[], text: string): void {
  if (text.length > 0) {
    segments.push(createTextSegment(text, segments.length));
  }
}

function createTextSegment(text: string, index: number): ConversationTextSegment {
  return {
    id: `text-${index}`,
    kind: 'text',
    text
  };
}

function normalizeProductCardRecord(record: unknown): ConversationProductCardView | null {
  if (!isRecord(record)) {
    return null;
  }

  const id = firstString(record['sku'], record['id']);
  const name = firstString(record['name'], record['title']);
  const url = firstString(record['url'], record['productUrl'], record['product_url'], record['link'], record['href']);
  if (!name || !isUsableProductUrl(url)) {
    return null;
  }

  const stableId = id || buildFallbackId(name, url);
  const normalized: ConversationProductCardView = {
    ...record,
    id: stableId,
    sku: stableId,
    name,
    url,
    description: firstString(record['description']),
    imageUrl: firstImageUrl(record['imageUrl'], record['image_url'], record['image'], record['images'])
  };

  const presentation = firstString(
    record['presentation'],
    record['layout'],
    record['variant'],
    record['displayVariant'],
    record['cardLayout'],
    record['cardPresentation']
  );
  if (presentation) {
    normalized.presentation = normalizeProductCardPresentation(presentation);
  }

  const priceText = firstString(record['priceText'], record['priceDisplay'], record['price_display']);
  if (priceText) {
    normalized.priceText = priceText;
  } else if (record['price'] !== undefined && record['price'] !== null && `${record['price']}`.trim().length > 0) {
    normalized.price = `${record['price']}`.trim();
  } else if (record['priceValue'] !== undefined && record['priceValue'] !== null) {
    normalized.price = `${record['priceValue']}`.trim();
  }

  const salePriceText = firstString(
    record['salePriceText'],
    record['sale_price_text'],
    record['discountedPriceText'],
    record['discounted_price_text']
  );
  if (salePriceText) {
    normalized.salePriceText = salePriceText;
  } else if (record['salePrice'] !== undefined && record['salePrice'] !== null) {
    normalized.salePrice = `${record['salePrice']}`.trim();
  } else if (record['sale_price'] !== undefined && record['sale_price'] !== null) {
    normalized.sale_price = `${record['sale_price']}`.trim();
  } else if (record['salePriceValue'] !== undefined && record['salePriceValue'] !== null) {
    normalized.salePrice = `${record['salePriceValue']}`.trim();
  }

  const regularPriceText = firstString(
    record['regularPriceText'],
    record['regular_price_text'],
    record['originalPriceText'],
    record['original_price_text']
  );
  if (regularPriceText) {
    normalized.regularPriceText = regularPriceText;
  } else if (record['regularPrice'] !== undefined && record['regularPrice'] !== null) {
    normalized.regularPrice = `${record['regularPrice']}`.trim();
  } else if (record['regular_price'] !== undefined && record['regular_price'] !== null) {
    normalized.regular_price = `${record['regular_price']}`.trim();
  } else if (record['regularPriceValue'] !== undefined && record['regularPriceValue'] !== null) {
    normalized.regularPrice = `${record['regularPriceValue']}`.trim();
  }

  const currency = firstString(record['currency'], record['priceCurrency'], record['priceSuffix']);
  if (currency) {
    normalized.currency = currency;
  }

  return normalized;
}

function dedupeProductCards(cards: ConversationProductCardView[]): ConversationProductCardView[] {
  const seen = new Set<string>();
  const uniqueCards: ConversationProductCardView[] = [];

  for (const card of cards || []) {
    const key = productCardIdentityKey(card);
    if (key && seen.has(key)) {
      continue;
    }
    if (key) {
      seen.add(key);
    }
    uniqueCards.push(card);
  }

  return uniqueCards;
}

function productCardIdentityKey(card: ConversationProductCardView): string {
  const sku = firstString(card.sku, card.id);
  if (sku) {
    return `sku:${sku.toLowerCase()}`;
  }

  const normalizedUrl = normalizeExactProductUrl(firstString(card.url));
  if (normalizedUrl) {
    return `url:${normalizedUrl}`;
  }

  const name = firstString(card.name);
  return name ? `name:${name.toLowerCase()}` : '';
}

function normalizeExactProductUrl(value: string): string {
  if (!value) {
    return '';
  }

  try {
    return new URL(value).toString().replace(/\/+$/g, '').toLowerCase();
  } catch {
    return value.replace(/\/+$/g, '').toLowerCase();
  }
}

function isProductCardFunctionName(value: string): boolean {
  return value === 'display_product_card' || value === 'display_product_cards';
}

function hasTextOffset(update: UnknownRecord): boolean {
  const value = firstDefined(update['TextOffset'], update['textOffset']);
  const parsed = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(parsed);
}

function readTextOffset(update: UnknownRecord): number {
  const value = firstDefined(update['TextOffset'], update['textOffset']);
  const parsed = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

function readTimestamp(update: UnknownRecord): number {
  const value = firstDefined(update['TimestampUtc'], update['timestampUtc']);
  if (!value) {
    return 0;
  }

  const parsed = Date.parse(`${value}`);
  return Number.isFinite(parsed) ? parsed : 0;
}

function isUsableProductUrl(value: unknown): boolean {
  const text = firstString(value);
  if (!text || isPlaceholderUrl(text)) {
    return false;
  }

  try {
    const baseHref = typeof window !== 'undefined' ? window.location?.href : undefined;
    const url = new URL(text, baseHref);
    return url.protocol === 'http:' || url.protocol === 'https:';
  } catch {
    return false;
  }
}

function isPlaceholderUrl(value: unknown): boolean {
  const text = firstString(value).toLowerCase();
  return !text ||
    text.includes('legg_inn') ||
    text.includes('placeholder') ||
    text.includes('todo') ||
    text.includes('example.invalid') ||
    text.includes('{{') ||
    text.includes('}}');
}

function normalizePresentationToken(value: unknown): string {
  return `${value ?? ''}`
    .trim()
    .replace(/([a-z0-9])([A-Z])/g, '$1-$2')
    .replace(/[\s_]+/g, '-')
    .toLowerCase();
}

function firstString(...values: unknown[]): string {
  for (const value of values) {
    const text = `${value ?? ''}`.trim();
    if (text.length > 0) {
      return text;
    }
  }
  return '';
}

function firstDefined(...values: unknown[]): unknown {
  for (const value of values) {
    if (value !== undefined && value !== null) {
      return value;
    }
  }
  return undefined;
}

function firstImageUrl(...values: unknown[]): string {
  for (const value of values) {
    if (Array.isArray(value)) {
      const nested = firstImageUrl(...value);
      if (nested) {
        return nested;
      }
      continue;
    }

    if (isRecord(value)) {
      const nested = firstString(value['url'], value['src'], value['href']);
      if (isUsableProductUrl(nested)) {
        return nested;
      }
      continue;
    }

    const text = firstString(value);
    const imageUrl = text.split(',').map(part => part.trim()).find(part => isUsableProductUrl(part));
    if (imageUrl) {
      return imageUrl;
    }
  }

  return '';
}

function buildFallbackId(name: string, url: string): string {
  const seed = `${name}|${url}`
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');
  return `ai-${seed.slice(0, 48) || 'card'}`;
}

function tryParseJson(value: string): unknown {
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
}

function isRecord(value: unknown): value is UnknownRecord {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}
